import React, { Suspense, lazy, startTransition, useCallback, useEffect, useRef, useState } from "react";
import { fetchCaseCards, fetchPortfolio, getApiBase } from "./lib/api";
import { createMockCaseCards, createMockPortfolio } from "./lib/mockData";
import { getEdgeEndpointId } from "./lib/utils";
import type {
  CaseCard,
  CompanyId,
  GraphEdge,
  GraphNode,
  PortfolioResponse,
} from "./types";
import type { TabKey } from "./types";

// Lazy-load the heavy tab views. Each one pulls in d3 and/or the ~725 KB
// auditData.ts snapshot, so code-splitting them keeps the initial bundle lean.
const EffectGraph = lazy(() => import("./components/EffectGraph"));
const MetricsView = lazy(() => import("./components/MetricsView"));
const PitchView = lazy(() => import("./components/PitchView"));
const PortfolioView = lazy(() => import("./components/PortfolioView"));
const ForwardTrackView = lazy(() => import("./components/ForwardTrackView"));
const BriefingView = lazy(() => import("./components/BriefingView"));
const HoldingView = lazy(() => import("./components/HoldingView"));

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "today",          label: "Today"               },
  { key: "pitch",          label: "About Ripple"       },
  { key: "metrics",        label: "Financial Metrics"   },
  // The "allocation" slot now renders the lean Portfolio overview (key kept to avoid a types.ts change).
  { key: "allocation",     label: "Portfolio"           },
  { key: "forward-track",  label: "Forward Track"       },
];

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : "Unknown error";
}

export default function App() {
  const [graphData, setGraphData] = useState<PortfolioResponse | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("today");
  const [graphReturnTo, setGraphReturnTo] = useState<TabKey>("today");
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<GraphEdge | null>(null);
  const [selectedHolding, setSelectedHolding] = useState<CompanyId | null>(null);
  const [caseCards, setCaseCards] = useState<CaseCard[]>([]);
  const [caseCardsLoading, setCaseCardsLoading] = useState(false);
  const [caseCardsError, setCaseCardsError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const graphSectionRef = useRef<HTMLDivElement | null>(null);
  // Always holds the current tab so handleBriefingNavigate avoids a stale closure
  // when capturing the graph drill-down origin.
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  useEffect(() => {
    const loadPortfolio = async () => {
      try {
        const response = await fetchPortfolio();
        setGraphData(response);
        setNotice(`Connected to ${getApiBase()}.`);
      } catch (error) {
        console.error(error);
        const mockPortfolio = await createMockPortfolio();
        setGraphData(mockPortfolio);
        setNotice(
          `Portfolio API unavailable: ${describeError(error)}. The dashboard is using mock portfolio data.`
        );
      }
    };

    void loadPortfolio();
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  const handleNodeSelect = useCallback((node: GraphNode) => {
    setSelectedNode(node);
    setSelectedEdge(null);
  }, []);

  const handleEdgeSelect = useCallback(async (edge: GraphEdge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
    setCaseCardsLoading(true);
    setCaseCardsError(null);

    const source = getEdgeEndpointId(edge.source);
    const target = getEdgeEndpointId(edge.target);

    try {
      const response = await fetchCaseCards(source, edge.source_metric, target);
      setCaseCards(response.cards);
    } catch (error) {
      console.error(error);
      setCaseCards(createMockCaseCards(source, edge.source_metric, target).cards);
      setCaseCardsError(
        `Live case cards unavailable: ${describeError(error)}. Showing mock historical cards for this edge.`
      );
    } finally {
      setCaseCardsLoading(false);
    }
  }, []);

  // Network-lens cards drill down into the (now demoted) force graph, focusing the edge.
  const handleBriefingNavigate = useCallback(
    (tab: TabKey, focus?: { source: string; target: string }) => {
      if (tab === "graph") {
        // Remember where the drill-down started so the graph's Back button returns there.
        setGraphReturnTo(activeTabRef.current === "holding" ? "holding" : "today");
        if (focus && graphData) {
          const edge = graphData.edges.find((e) => {
            const s = getEdgeEndpointId(e.source);
            const t = getEdgeEndpointId(e.target);
            return (s === focus.source && t === focus.target) || (s === focus.target && t === focus.source);
          });
          if (edge) void handleEdgeSelect(edge);
        }
      }
      startTransition(() => setActiveTab(tab));
    },
    [graphData, handleEdgeSelect]
  );

  // Open a holding's detail page (entry: clicking a ticker in the masthead / holdings rail).
  const handleHoldingSelect = useCallback((id: string) => {
    setSelectedHolding(id as CompanyId);
    startTransition(() => setActiveTab("holding"));
  }, []);

  return (
    <div className="app-shell">
      <div className="ambient-orb ambient-orb-left" />
      <div className="ambient-orb ambient-orb-right" />

      <header className="masthead">
        <div className="masthead-copy">
          <h1
            className="ripple-logo"
            role="button"
            tabIndex={0}
            onClick={() => startTransition(() => setActiveTab("pitch"))}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                startTransition(() => setActiveTab("pitch"));
              }
            }}
            aria-label="Ripple — return to About Ripple"
            title="Return to About Ripple"
          >
            Ripple
          </h1>
          <div className="masthead-subtitle">AI-Powered Portfolio Risk Surveillance from SEC Filings</div>
          <div className="masthead-subsection">
            <span style={{ marginRight: 2 }}>PORTFOLIO:</span>
            {[
              { id: "apple", t: "AAPL" },
              { id: "nvidia", t: "NVDA" },
              { id: "tsmc", t: "TSM" },
              { id: "google", t: "GOOGL" },
              { id: "microsoft", t: "MSFT" },
            ].map((h) => (
              <button
                key={h.id}
                type="button"
                onClick={() => handleHoldingSelect(h.id)}
                title={`Open ${h.t} holding detail`}
                style={{ fontFamily: "var(--font-mono)", fontSize: "0.82rem", fontWeight: 700, color: "var(--accent)", background: "var(--accent-wash)", border: "1px solid var(--border)", borderRadius: 16, padding: "3px 10px", cursor: "pointer" }}
              >
                {h.t}
              </button>
            ))}
          </div>
          <div className="export-bar">
            <span className="export-bar-label">Exports</span>
            <a
              className="export-button"
              href="/ripple_portfolio_analysis.xlsx"
              download="ripple_portfolio_analysis.xlsx"
              title="Download the 5-sheet Excel workbook"
            >
              Excel Workbook
            </a>
            <a
              className="export-button"
              href="/ripple_audit_export.json"
              download="ripple_audit_export.json"
              title="Download the full audit JSON (986 KB)"
            >
              Audit JSON
            </a>
          </div>
        </div>
      </header>

      {notice && (
        <div className="notice-banner" role="status" aria-live="polite">
          {notice}
        </div>
      )}

      <nav className="tab-row" aria-label="Dashboard views">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={activeTab === tab.key ? "tab active" : "tab"}
            onClick={() => startTransition(() => setActiveTab(tab.key))}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <main className="content-frame">
        <Suspense fallback={<div className="panel-surface" style={{ padding: 48, textAlign: "center", color: "var(--text-muted, #3d3960)" }}>Loading…</div>}>
          {activeTab === "today" && (
            <BriefingView onNavigate={handleBriefingNavigate} />
          )}

          {activeTab === "holding" && selectedHolding && (
            <HoldingView
              companyId={selectedHolding}
              onBack={() => startTransition(() => setActiveTab("today"))}
              onSelectHolding={handleHoldingSelect}
              onNavigate={handleBriefingNavigate}
            />
          )}

          {activeTab === "pitch" && (
            <PitchView onNavigate={(tab) => startTransition(() => setActiveTab(tab))} />
          )}

          {activeTab === "graph" && graphData && (
            <div ref={graphSectionRef} className="graph-top-section">
              <button
                type="button"
                onClick={() => startTransition(() => setActiveTab(graphReturnTo))}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 14, padding: "6px 12px", fontSize: "0.85rem", fontWeight: 600, color: "var(--accent)", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, cursor: "pointer" }}
              >
                {graphReturnTo === "holding" ? "← Back to holding" : "← Back to briefing"}
              </button>
              <EffectGraph
                data={graphData}
                selectedNode={selectedNode}
                selectedEdge={selectedEdge}
                caseCards={caseCards}
                caseCardsLoading={caseCardsLoading}
                caseCardsError={caseCardsError}
                onNodeSelect={handleNodeSelect}
                onEdgeSelect={handleEdgeSelect}
                onClearSelection={clearSelection}
              />
            </div>
          )}

          {activeTab === "graph" && !graphData && (
            <div className="panel-surface" style={{ padding: 48, textAlign: "center", color: "var(--text-muted)" }}>
              Loading portfolio…
            </div>
          )}

          {activeTab === "metrics" && (
            <MetricsView liveData={null} />
          )}

          {activeTab === "allocation" && <PortfolioView onHolding={handleHoldingSelect} />}

          {activeTab === "forward-track" && <ForwardTrackView />}
        </Suspense>
      </main>

    </div>
  );
}
